import { createContext, useContext, type ReactNode } from 'react';

export type FieldHelpResolver = (fieldId: string, label: string) => ReactNode;
export type TermHelpResolver = (term: string) => ReactNode;
interface TermHelpContextValue {
  resolve: TermHelpResolver;
  glossary: readonly string[];
}

const FieldHelpContext = createContext<FieldHelpResolver | null>(null);
const TermHelpContext = createContext<TermHelpContextValue | null>(null);

export function FieldHelpProvider({
  resolve,
  children
}: {
  resolve: FieldHelpResolver;
  children: ReactNode;
}) {
  return <FieldHelpContext.Provider value={resolve}>{children}</FieldHelpContext.Provider>;
}

export function useFieldHelp(fieldId: string, label: string): ReactNode {
  return useContext(FieldHelpContext)?.(fieldId, label) ?? null;
}

export function TermHelpProvider({
  resolve,
  glossary = [],
  children
}: {
  resolve: TermHelpResolver;
  glossary?: readonly string[];
  children: ReactNode;
}) {
  return (
    <TermHelpContext.Provider value={{ resolve, glossary }}>{children}</TermHelpContext.Provider>
  );
}

export function useTermHelp(term: string): ReactNode {
  return useContext(TermHelpContext)?.resolve(term) ?? null;
}

export function useTermGlossary(): readonly string[] {
  return useContext(TermHelpContext)?.glossary ?? [];
}

export function HelpTerm({
  term,
  children,
  className
}: {
  term: string;
  children?: ReactNode;
  className?: string;
}) {
  const help = useTermHelp(term);
  return (
    <span className={className ? `dd-help-term ${className}` : 'dd-help-term'}>
      <span>{children ?? term}</span>
      {help}
    </span>
  );
}

export function TermHelpIcon({ term }: { term: string }) {
  return useTermHelp(term);
}
